import { useState } from "react";
import { toast } from "sonner";
import { TypedDateInput } from "@/components/TypedDateInput";
import { formatBusinessDate } from "@/utils/datetime";
import { useAuth } from "@/auth/AuthContext";
import { useColumnResize } from "@/hooks/useColumnResize";
import { useSpreadsheetGrid } from "@/hooks/useSpreadsheetGrid";
import { PaymentAttachmentCell } from "@/components/PayrollPaymentAttachmentsCell";

import { addDays, errorMessage, exportPaymentsCsv, isDueSoon, isOverdue, money, TERM_DAYS } from "./payrollUtils";

const CONTRACTOR_HISTORY_COLUMNS = [
  { key: "submitted_date", label: "Submitted", align: "left" },
  { key: "due_date",       label: "Due",       align: "left" },
  { key: "paid_date",      label: "Paid",      align: "left" },
  { key: "amount",         label: "Amount",    align: "right" },
  { key: "status",         label: "Status",    align: "left" },
  { key: "job",            label: "Job",       align: "left" },
  { key: "notes",          label: "Notes",     align: "left" },
  { key: "photos",         label: "Photos",    align: "center" },
  { key: "documents",      label: "Docs",      align: "center" },
];

const IN_HOUSE_HISTORY_COLUMNS = [
  { key: "payment_date",   label: "Paid",   align: "left" },
  { key: "amount",         label: "Amount", align: "right" },
  { key: "rate_used",      label: "Rate",   align: "right" },
  { key: "hours",          label: "Hours",  align: "right" },
  { key: "days",           label: "Days",   align: "right" },
  { key: "status",         label: "Status", align: "left" },
  { key: "notes",          label: "Notes",  align: "left" },
  { key: "photos",         label: "Photos", align: "center" },
  { key: "documents",      label: "Docs",   align: "center" },
];

const StatusChip = ({ item }) => {
  const overdue = isOverdue(item);
  const dueSoon = isDueSoon(item);
  let label = item.status === "paid" ? "Paid" : "Unpaid";
  let color = item.status === "paid" ? "#166534" : "#334155";
  if (overdue) { label = "Overdue"; color = "#dc2626"; }
  else if (dueSoon) { label = "Due soon"; color = "#b45309"; }
  return (
    <strong data-testid={`payroll-payment-status-${item.id}`} style={{ color }}>{label}</strong>
  );
};

const PaymentHistoryTable = ({ payments, tab, userId, testIdSuffix = "upcoming", onMarkPaid, onEdit }) => {
  const columns = tab === "contractor" ? CONTRACTOR_HISTORY_COLUMNS : IN_HOUSE_HISTORY_COLUMNS;
  const columnResize = useColumnResize({ boardKey: `payroll-history-${tab}-${testIdSuffix}`, userId });
  const grid = useSpreadsheetGrid({
    onEnter: (rowKey) => {
      const item = payments.find((p) => p.id === rowKey);
      if (item) onEdit(item);
    },
  });

  const renderCell = (col, item) => {
    const props = grid.cellProps({ rowKey: item.id, colKey: col.key });
    const testId = `payroll-payment-${col.key}-${item.id}`;
    const align = col.align === "right"
      ? { textAlign: "right", fontVariantNumeric: "tabular-nums" }
      : { textAlign: col.align };
    switch (col.key) {
      case "amount":
        return <td {...props} data-testid={testId} key={col.key} style={{ ...align, ...(props.style || {}) }}>{money(item.amount)}</td>;
      case "rate_used":
        return <td {...props} data-testid={testId} key={col.key} style={{ ...align, ...(props.style || {}) }}>{item.rate_used ? money(item.rate_used) : "—"}</td>;
      case "hours":
      case "days":
        return <td {...props} data-testid={testId} key={col.key} style={{ ...align, ...(props.style || {}) }}>{item[col.key] || "—"}</td>;
      case "submitted_date":
      case "due_date":
      case "paid_date":
      case "payment_date":
        return <td {...props} data-testid={testId} key={col.key} style={{ ...align, ...(props.style || {}) }}>{formatBusinessDate(item[col.key])}</td>;
      case "status":
        return <td {...props} data-testid={testId} key={col.key} style={{ ...align, ...(props.style || {}) }}><StatusChip item={item} /></td>;
      case "job":
        return (
          <td
            {...props}
            data-testid={testId}
            key={col.key}
            style={{ ...align, maxWidth: "12rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...(props.style || {}) }}
            title={item.job || ""}
          >
            {item.job || "—"}
          </td>
        );
      case "notes":
        return (
          <td
            {...props}
            data-testid={testId}
            key={col.key}
            style={{ ...align, color: "#475569", maxWidth: "18rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...(props.style || {}) }}
            title={item.notes || ""}
          >
            {item.notes || ""}
          </td>
        );
      case "photos":
        return (
          <PaymentAttachmentCell
            cellProps={grid.cellProps}
            key="photos"
            mode="photos"
            paymentId={item.id}
          />
        );
      case "documents":
        return (
          <PaymentAttachmentCell
            cellProps={grid.cellProps}
            key="documents"
            mode="documents"
            paymentId={item.id}
          />
        );
      default:
        return (
          <td {...props} data-testid={testId} key={col.key} style={{ ...align, ...(props.style || {}) }}>
            {item[col.key] || "—"}
          </td>
        );
    }
  };

  return (
    <div className="board-table-scroll" data-testid={`payroll-payment-history-table-${testIdSuffix}`}>
      <table className="operations-data-table spreadsheet-grid" {...grid.tableProps}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col" style={{ textAlign: col.align, ...(columnResize.headerStyle(col.key) || {}) }}>
                <span data-mobile-label={col.label}>{col.label}</span>
                <button
                  aria-label={`Resize ${col.label} column`}
                  className="col-resize-handle"
                  data-testid={`payroll-history-col-resize-${col.key}`}
                  onDoubleClick={() => columnResize.resetColumn(col.key)}
                  onPointerDown={(event) => columnResize.beginDrag(event, col.key)}
                  tabIndex={-1}
                  title="Drag to resize · double-click to reset"
                  type="button"
                />
              </th>
            ))}
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((item) => {
            const overdue = isOverdue(item);
            const dueSoon = !overdue && isDueSoon(item);
            return (
              <tr
                data-testid={`payroll-payment-${item.id}`}
                key={item.id}
                style={overdue ? { boxShadow: "inset 3px 0 0 #dc2626" } : dueSoon ? { boxShadow: "inset 3px 0 0 #f59e0b" } : undefined}
              >
                {columns.map((col) => renderCell(col, item))}
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {item.status === "unpaid" && (
                    <button
                      className="invoice-primary-button"
                      data-testid={`payroll-mark-paid-${item.id}`}
                      onClick={() => onMarkPaid(item)}
                      style={{ marginRight: "0.35rem" }}
                      type="button"
                    >
                      Mark paid
                    </button>
                  )}
                  <button data-testid={`payroll-edit-payment-${item.id}`} onClick={() => onEdit(item)} type="button">
                    Edit
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PaymentTotals = ({ payments }) => {
  const paidTotal = payments
    .filter((item) => item.status === "paid")
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const unpaidTotal = payments
    .filter((item) => item.status === "unpaid")
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const overdue = payments.filter(isOverdue);
  const overdueTotal = overdue.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const dueSoon = payments.filter(isDueSoon);
  const dueSoonTotal = dueSoon.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  return (
    <div className="workspace-grid" data-testid="payroll-totals">
      <span data-testid="payroll-total-paid">Paid: {money(paidTotal)}</span>
      <span data-testid="payroll-total-unpaid">Unpaid: {money(unpaidTotal)}</span>
      <strong data-testid="payroll-total-outstanding">Outstanding balance: {money(unpaidTotal)}</strong>
      {dueSoon.length > 0 && (
        <strong data-testid="payroll-total-due-soon" style={{ color: "#b45309" }}>
          Due soon: {dueSoon.length} ({money(dueSoonTotal)})
        </strong>
      )}
      {overdue.length > 0 && (
        <strong data-testid="payroll-total-overdue" style={{ color: "#dc2626" }}>
          Overdue: {overdue.length} ({money(overdueTotal)})
        </strong>
      )}
    </div>
  );
};

export const PayrollProfileDetail = ({
  detail,
  tab,
  payment,
  setPayment,
  dueEdited,
  setDueEdited,
  editingPaymentId,
  setEditingPaymentId,
  savePayment,
  toggleActive,
  markPaid,
  beginEditPayment,
  resetPayment,
  onAddPayment,
}) => {
  const { user } = useAuth();
  const [paidOpen, setPaidOpen] = useState(false);
  const [exportingHistory, setExportingHistory] = useState(false);
  const exportHistory = async () => {
    if (exportingHistory) return;
    setExportingHistory(true);
    try {
      await exportPaymentsCsv(detail.profile, detail.payments);
    } catch (requestError) {
      toast.error(errorMessage(requestError, "Payment history could not be exported."));
    } finally {
      setExportingHistory(false);
    }
  };
  const upcomingPayments = detail.payments.filter((p) => p.status !== "paid");
  const paidPayments = detail.payments.filter((p) => p.status === "paid");
  return (
  <section data-testid="payroll-profile-detail">
    <h2 data-testid="payroll-profile-title">{detail.profile.name}</h2>
    <p data-testid="payroll-profile-meta">
      {detail.profile.active ? "Active" : "Inactive"}
      {tab === "in_house"
        ? ` · Default rate ${money(detail.profile.default_rate)}`
        : ` · Term ${detail.profile.payment_term?.replace("_", " ")}`}
      {detail.profile.tax_info_present && (
        <span data-testid="payroll-tax-masked"> · SSN/Tax {detail.profile.tax_info_masked}</span>
      )}
    </p>
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      <button
        className="invoice-primary-button"
        data-testid="payroll-record-add-payment"
        onClick={() => onAddPayment(detail.profile)}
        type="button"
      >
        + Payment
      </button>
      <button data-testid="payroll-toggle-active" onClick={toggleActive} type="button">
        {detail.profile.active ? "Set inactive" : "Reactivate"}
      </button>
      <button
        data-testid="payroll-export-csv"
        disabled={detail.payments.length === 0 || exportingHistory}
        onClick={exportHistory}
        type="button"
      >
        Export CSV
      </button>
    </div>

    <form className="field-note-form" data-testid="payroll-payment-form" onSubmit={savePayment}>
      <label htmlFor="payroll-payment-amount">
        Amount
        <input
          data-testid="payroll-payment-amount"
          id="payroll-payment-amount"
          min="0"
          onChange={(event) => setPayment({ ...payment, amount: event.target.value })}
          required
          step="0.01"
          type="number"
          value={payment.amount}
        />
      </label>
      {tab === "in_house" ? (
        <>
          <label htmlFor="payroll-payment-date">
            Payment date
            <TypedDateInput
              data-testid="payroll-payment-date"
              id="payroll-payment-date"
              onChange={(value) => setPayment({ ...payment, payment_date: value })}
              value={payment.payment_date}
            />
          </label>
          <label htmlFor="payroll-payment-rate">
            Rate used (optional)
            <input
              data-testid="payroll-payment-rate"
              id="payroll-payment-rate"
              min="0"
              onChange={(event) => setPayment({ ...payment, rate_used: event.target.value })}
              step="0.01"
              type="number"
              value={payment.rate_used}
            />
          </label>
          <label htmlFor="payroll-payment-hours">
            Hours (optional)
            <input
              data-testid="payroll-payment-hours"
              id="payroll-payment-hours"
              min="0"
              onChange={(event) => setPayment({ ...payment, hours: event.target.value })}
              step="0.01"
              type="number"
              value={payment.hours}
            />
          </label>
          <label htmlFor="payroll-payment-days">
            Days (optional)
            <input
              data-testid="payroll-payment-days"
              id="payroll-payment-days"
              min="0"
              onChange={(event) => setPayment({ ...payment, days: event.target.value })}
              step="0.01"
              type="number"
              value={payment.days}
            />
          </label>
        </>
      ) : (
        <>
          <label htmlFor="payroll-submitted-date">
            Submitted date
            <TypedDateInput
              data-testid="payroll-submitted-date"
              id="payroll-submitted-date"
              onChange={(value) =>
                setPayment((prev) => ({
                  ...prev,
                  submitted_date: value,
                  due_date: dueEdited
                    ? prev.due_date
                    : addDays(value, TERM_DAYS[detail.profile.payment_term] || 14),
                }))
              }
              value={payment.submitted_date}
            />
          </label>
          <label htmlFor="payroll-due-date">
            Due date (auto from term; editable)
            <TypedDateInput
              data-testid="payroll-due-date"
              id="payroll-due-date"
              onChange={(value) => {
                setDueEdited(true);
                setPayment((prev) => ({ ...prev, due_date: value }));
              }}
              value={payment.due_date}
            />
          </label>
          <label htmlFor="payroll-paid-date">
            Paid date
            <TypedDateInput
              data-testid="payroll-paid-date"
              id="payroll-paid-date"
              onChange={(value) => setPayment({ ...payment, paid_date: value })}
              value={payment.paid_date}
            />
          </label>
          <label htmlFor="payroll-job">
            Job
            <input
              data-testid="payroll-job"
              id="payroll-job"
              onChange={(event) => setPayment({ ...payment, job: event.target.value })}
              placeholder="e.g. Maple St bore"
              value={payment.job || ""}
            />
          </label>
        </>
      )}
      <label htmlFor="payroll-payment-status">
        Status
        <select
          data-testid="payroll-payment-status"
          id="payroll-payment-status"
          onChange={(event) => setPayment({ ...payment, status: event.target.value })}
          value={payment.status}
        >
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
        </select>
      </label>
      <label className="wide-field" htmlFor="payroll-payment-notes">
        Notes
        <textarea
          data-testid="payroll-payment-notes"
          id="payroll-payment-notes"
          onChange={(event) => setPayment({ ...payment, notes: event.target.value })}
          value={payment.notes}
        />
      </label>
      <div className="active-job-edit-actions" data-testid="payroll-payment-actions">
        <button className="invoice-primary-button" data-testid="payroll-save-payment" type="submit">
          {editingPaymentId ? "Save payment" : "Record payment"}
        </button>
        {editingPaymentId && (
          <button
            data-testid="payroll-cancel-edit"
            onClick={() => {
              setEditingPaymentId("");
              setDueEdited(false);
              resetPayment();
            }}
            type="button"
          >
            Cancel edit
          </button>
        )}
      </div>
    </form>

    <section data-testid="payroll-payment-history">
      <h3>Upcoming payments</h3>
      <PaymentTotals payments={detail.payments} />
      {detail.payments.length === 0 && <p data-testid="payroll-history-empty">No payments recorded yet.</p>}
      {detail.payments.length > 0 && upcomingPayments.length === 0 && (
        <p data-testid="payroll-history-no-upcoming" style={{ color: "#64748b" }}>
          Nothing upcoming. All payments for this contractor are paid — see the Paid folder below.
        </p>
      )}
      {upcomingPayments.length > 0 && (
        <PaymentHistoryTable
          onEdit={beginEditPayment}
          onMarkPaid={markPaid}
          payments={upcomingPayments}
          tab={tab}
          testIdSuffix="upcoming"
          userId={user?.id}
        />
      )}

      {paidPayments.length > 0 && (
        <section data-testid="payroll-paid-folder" style={{ marginTop: "0.85rem" }}>
          <button
            data-testid="payroll-paid-folder-toggle"
            onClick={() => setPaidOpen((open) => !open)}
            style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem" }}
            type="button"
          >
            <span>{paidOpen ? "▾" : "▸"} Paid</span>
            <strong data-testid="payroll-paid-folder-count">{paidPayments.length}</strong>
          </button>
          {paidOpen && (
            <PaymentHistoryTable
              onEdit={beginEditPayment}
              onMarkPaid={markPaid}
              payments={paidPayments}
              tab={tab}
              testIdSuffix="paid"
              userId={user?.id}
            />
          )}
        </section>
      )}
    </section>
  </section>
  );
};
